import { useCallback, useEffect, useState } from 'react';
import { tabSharedWorker } from '../../core/sharedWorker/sharedWorkerManager';
import { toggleMasterTab } from '../../core/redux/auth/authSlice';
import {
  SharedWorkerResponse,
  SharedWorkerMessage,
} from '../../core/sharedWorker/types';
import { useAppDispatch, useAppSelector } from '../../core/hooks/storeHooks';
import { logoutUser } from '../../core/redux/auth/thunks/user';
import { useNavigate } from 'react-router-dom';
import sessionStorageSingleton from '../../core/configs/sessionStorageSingleton';
import {
  syncTabChanges,
  tabSyncManualModeDiscardChanges,
} from '../../sheet/redux/sagas/syncDeprecated/syncActions';
import {
  disableManualSave,
  enableManualSave,
  manualModeChangesSaved,
} from '../../sheet/redux/sheetsManagerSlice';
import { getManualModeState } from '../../core/utils/auth/authUtils';
import { sheetSharedStorageWorker } from '../../sheet/storage';

export default () => {
  const userId = useAppSelector((state) => state.auth.user.id);
  const userName = useAppSelector((state) => state.auth.user.fullname);
  const isMasterTab = useAppSelector((state) => state.auth.app.isMasterTab);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [masterTabConflict, toggleMasterTabConflict] = useState(false);
  const [manualModeConflict, toggleManualModeConflict] = useState(false);
  const unSavedChangesLength = useAppSelector(
    (state) => state.sheetsManager.unSavedHistory.length
  );

  const unSavedSheets = useAppSelector((state) => ({
    unSavedChanges: state.sheetsManager.unSavedChanges,
    unSavedHistory: state.sheetsManager.unSavedHistory,
  }));

  const navigateLogin = () => navigate('/login');

  useEffect(() => {
    if (isMasterTab) {
      document.title = `${userName.split(' ')[0]}'s Master Tab`;
    } else {
      document.title = `${userName.split(' ')[0]}'s Tab`;
    }
  }, [userId, userName, isMasterTab]);

  useEffect(() => {
    // document.title = 'Enigma';
    tabSharedWorker.port.onmessage = async (event: MessageEvent) => {
      const eventType = event.data.type;

      // console.log('event -- inside enigma', event);

      if (!eventType.length) {
        validateManualModeConflict(event, userId);
      }

      switch (eventType) {
        case SharedWorkerResponse.SetAsMasterTab:
          // document.title = `${userName.split(' ')[0]}'s Master Tab`;
          dispatch(toggleMasterTab(true));
          validateManualModeConflict(event, userId);

          break;
        case SharedWorkerResponse.MasterTabConflict:
          toggleMasterTabConflict(true);
          break;
        case SharedWorkerResponse.MasterTabConflictResolved:
          toggleMasterTabConflict(false);
          break;
        case SharedWorkerResponse.ForceLogout:
          dispatch(
            logoutUser({ navigateAuth: navigateLogin, forceLogout: true })
          );
          break;
        case SharedWorkerResponse.RequestFeKey:
          const privateKey = await sessionStorageSingleton.getPrivateKey();
          const directoryHandler =
            sessionStorageSingleton.getDirectoryHandler();
          tabSharedWorker.port.postMessage({
            type: SharedWorkerMessage.SendFeKey,
            userId,
            privateKey,
            directoryHandler,
            unSavedSheets,
          });
          break;
        case SharedWorkerResponse.ReceiveNotifiedChanges:
          if (event.data?.params)
            dispatch(
              syncTabChanges({
                ...event.data.params,
                userId: event.data.userId,
              })
            );
          break;
        case SharedWorkerResponse.UpdateManualSaveToggle:
          validateManualModeConflict(event, userId);
          updateManualSaveSharedToggle(event, userId);
          break;
        case SharedWorkerResponse.ReceiveManualModeAfterChangesSaved:
          const disable = event.data.disableManualSave;
          dispatch(
            manualModeChangesSaved({
              sheets: {},
              disable,
            })
          );
          break;
        case SharedWorkerResponse.ReceiveManualModeAfterChangesDiscard:
          dispatch(tabSyncManualModeDiscardChanges(event.data.params));
      }
    };
  }, [userId, unSavedSheets]);

  function validateManualModeConflict(event: MessageEvent, userId: string) {
    const manualModeEnabledUserId = event.data.manualModeEnabledUserId;
    if (manualModeEnabledUserId && manualModeEnabledUserId !== userId) {
      toggleManualModeConflict(true);
    }
    // else {
    //   toggleManualModeConflict(false);
    // }
  }

  function updateManualSaveSharedToggle(event: MessageEvent, userId: string) {
    const manualModeEnabledUserId = event.data.manualModeEnabledUserId;
    const isManualSave = event.data.isManualSave; // && manualModeEnabledUserId === userId
    // console.log("manualModeEnabledUserId", manualModeEnabledUserId)
    // console.log("userId", userId)
    if (!manualModeEnabledUserId || manualModeEnabledUserId === userId) {
      isManualSave
        ? dispatch(enableManualSave())
        : dispatch(disableManualSave());
    }
  }

  useEffect(() => {
    if (userId) {
      tabSharedWorker.port.postMessage({
        type: SharedWorkerMessage.StartAssignMasterTab,
        userId,
        isManualSave: getManualModeState(userId),
      });
    }
  }, []);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      // Send a message to the shared worker indicating that the current tab is closing
      tabSharedWorker.port.postMessage({
        type: SharedWorkerMessage.ShiftMasterTab,
        userId,
      });

      if (unSavedChangesLength > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    [unSavedChangesLength, userId]
  );

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('contextmenu', handleContextMenu);

    window.addEventListener('error', (e) => {
      if (e.message === 'ResizeObserver loop limit exceeded') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [handleBeforeUnload]);

  return {
    masterTabConflict,
    manualModeConflict,
  };
};
